import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The Countdown app's corner POPUP (feature 0041 V4) — a small bubble that appears after a delay on the
 * pages the seller targeted.
 *
 * It shares `countdownEngine` with the banner, the bar and the badge (one clock, one preset table), and
 * differs from them in exactly two ways, both deliberate:
 *  - it is CLIENT-ONLY (mounted `client:idle`, hidden until its delay elapses). Nothing about a bubble
 *    that appears ten seconds in belongs in the first HTML, and rendering it server-side would only cost
 *    a hydration mismatch for zero benefit;
 *  - dismissal is per visit and per campaign (`sessionStorage`), like the bar: closing means "not this
 *    visit", never "never again" — the campaign is still live and the next visit is a new chance.
 */
import { useEffect, useState } from 'react';
import { t } from '@aquilixshop/i18n';
import { floatingPaletteFor, pad2, parseProductCountdown, splitUnits, digitScale, floatingDigitTile, scaled, textScale, unitLabels, useCountdownTick, } from './countdownEngine.js';
const CORNERS = [
    'bottom-left',
    'bottom-right',
    'top-left',
    'top-right',
];
/** Defensive wire → payload; the CTA survives only with BOTH a label and a safe href (second lock). */
export function parseCountdownPopup(raw) {
    const base = parseProductCountdown(raw);
    if (!base)
        return undefined;
    const r = raw;
    const popupRaw = (typeof r.popup === 'object' && r.popup !== null ? r.popup : {});
    const ctaLabel = typeof popupRaw.ctaLabel === 'string' ? popupRaw.ctaLabel.trim() : '';
    const rawUrl = typeof popupRaw.ctaUrl === 'string' ? popupRaw.ctaUrl.trim() : '';
    const safeUrl = rawUrl.startsWith('//')
        ? ''
        : rawUrl.startsWith('/')
            ? rawUrl
            : /^https?:\/\//i.test(rawUrl)
                ? rawUrl
                : '';
    const cta = ctaLabel !== '' && safeUrl !== '' ? { ctaLabel, ctaUrl: safeUrl } : {};
    const delay = typeof popupRaw.delaySeconds === 'number' ? popupRaw.delaySeconds : 5;
    return {
        ...base,
        popup: {
            position: CORNERS.find((c) => c === popupRaw.position) ?? 'bottom-right',
            delaySeconds: Math.min(300, Math.max(0, Math.round(Number.isFinite(delay) ? delay : 5))),
            ...cta,
        },
    };
}
const DISMISS_PREFIX = 'aqx-cdpop-closed-';
function wasDismissed(campaignId) {
    try {
        return window.sessionStorage.getItem(DISMISS_PREFIX + campaignId) === '1';
    }
    catch {
        return false;
    }
}
function rememberDismissed(campaignId) {
    try {
        window.sessionStorage.setItem(DISMISS_PREFIX + campaignId, '1');
    }
    catch {
        /* private browsing — it simply reappears on the next page */
    }
}
export function CountdownPopup({ popup, locale, 
/** The dashboard preview passes false so the seller cannot lose the thing they are editing. */
dismissible = true, }) {
    const { remaining, hidden } = useCountdownTick(popup);
    const [shown, setShown] = useState(false);
    const [closed, setClosed] = useState(false);
    useEffect(() => {
        if (wasDismissed(popup.campaignId))
            return;
        const id = window.setTimeout(() => setShown(true), popup.popup.delaySeconds * 1000);
        return () => window.clearTimeout(id);
    }, [popup.campaignId, popup.popup.delaySeconds]);
    if (hidden || closed || !shown)
        return null;
    // A bubble floats over the page, like the bar: transparent presets become ink, colours repaired with it.
    const palette = floatingPaletteFor(popup);
    const background = palette.background;
    const text = palette.text;
    const type = textScale(popup.design, { size: 14, weight: 700, subtitle: 11 });
    // The clock's own size (04/10/2026) — the bubble has always painted 17px digits over a 7px label.
    const clock = digitScale(popup.design, 17);
    const tile = floatingDigitTile(popup.design, clock.scale);
    const units = splitUnits(remaining);
    const labels = unitLabels(locale);
    const cells = [
        { label: labels[0], value: units.days },
        { label: labels[1], value: units.hours },
        { label: labels[2], value: units.minutes },
        { label: labels[3], value: units.seconds },
    ];
    const [vertical, horizontal] = popup.popup.position.split('-');
    const wrapper = {
        position: 'fixed',
        [vertical]: 16,
        [horizontal]: 16,
        // Below the cart drawer (80) and the sales-pop overlay (60): a countdown must never cover either.
        zIndex: 55,
        background,
        color: text,
        borderRadius: 12,
        padding: '14px 34px 14px 16px',
        boxShadow: '0 10px 30px rgba(0,0,0,.25)',
        maxWidth: 320,
    };
    return (_jsxs("div", { "data-aqx-countdown-popup": popup.campaignId, style: wrapper, role: "region", "aria-label": popup.title ?? t({ locale }, 'a11y.countdown'), children: [popup.title ? (_jsx("div", { style: { fontSize: type.size, fontWeight: type.weight, lineHeight: 1.25 }, children: popup.title })) : null, popup.subtitle ? (_jsx("div", { style: { fontSize: type.subtitle, opacity: 0.72, marginTop: 1 }, children: popup.subtitle })) : null, _jsx("div", { dir: "ltr", style: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: scaled(7, clock.scale),
                    marginTop: 8,
                }, children: cells.map((cell, i) => (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: scaled(7, clock.scale) }, children: [i > 0 ? (_jsx("span", { style: { fontSize: scaled(13, clock.scale), fontWeight: 700, opacity: 0.6 }, children: ":" })) : null, _jsxs("span", { style: { display: 'flex', flexDirection: 'column', alignItems: 'center' }, children: [_jsx("span", { "data-aqx-cd-digit": "", style: {
                                        fontSize: clock.size,
                                        fontWeight: 700,
                                        color: palette.digit,
                                        fontVariantNumeric: 'tabular-nums',
                                        lineHeight: 1.1,
                                        ...tile,
                                    }, children: pad2(cell.value) }), _jsx("span", { style: { fontSize: scaled(7, clock.scale), letterSpacing: 1, opacity: 0.55 }, children: cell.label })] })] }, cell.label))) }), popup.visitors ? (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }, children: [_jsx("span", { style: { width: 6, height: 6, borderRadius: 3, background: '#34d17b' } }), _jsx("span", { style: { fontSize: 12, fontWeight: 700 }, children: popup.visitors.count }), _jsx("span", { style: { fontSize: 11, opacity: 0.75 }, children: popup.visitors.message })] })) : null, popup.popup.ctaLabel && popup.popup.ctaUrl ? (_jsx("a", { href: popup.popup.ctaUrl, style: {
                    display: 'inline-block',
                    marginTop: 10,
                    background: palette.button,
                    color: palette.buttonText,
                    fontSize: 12,
                    fontWeight: 700,
                    padding: '7px 14px',
                    borderRadius: 6,
                    textDecoration: 'none',
                }, children: popup.popup.ctaLabel })) : null, dismissible ? (_jsx("button", { type: "button", "aria-label": t({ locale }, 'a11y.close'), onClick: () => {
                    rememberDismissed(popup.campaignId);
                    setClosed(true);
                }, style: {
                    position: 'absolute',
                    insetInlineEnd: 10,
                    top: 10,
                    background: 'transparent',
                    border: 0,
                    color: text,
                    opacity: 0.5,
                    fontSize: 15,
                    lineHeight: 1,
                    cursor: 'pointer',
                    padding: 2,
                }, children: "\u00D7" })) : null] }));
}
//# sourceMappingURL=CountdownPopup.js.map